/**
 * Landing hero intro: the seal draws in over the title, then the industry-leaders band and its cards
 * arrive, all on one Web Animations timeline. Timing and curves come from the designers' prototype.
 * A phone plays the same moments on a shorter clock: its logo scene is the seal big in the middle of
 * the screen with the title under it, then one glide into the resting row; the trust lines arrive as
 * the pair lands, and the marquee rows come in where a wider screen brings its cards.
 *
 * The resting layout is the server-rendered one. Every move is a transform or opacity from an offset
 * measured at start, so nothing reflows. The parts start hidden through their hero-intro-pending:
 * utilities (the variant is in app.css), keyed on a wrapper attribute dropped in the same task the
 * animations start.
 */
import {focalCarousel} from '../../behaviors/focalCarousel.js';
import {drawSeal} from '../../components/motion/seal.js';
import {isMobile, BREAKPOINTS} from '../../modules/breakpoint.js';
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {createRun} from '../../modules/motion/run.js';
import {settle} from '../../modules/motion/settle.js';
import {forwardAbort} from '../../utils/abort.js';

const SELECTOR = Object.freeze({
    root: '.js-heroIntro',
    seal: '.js-heroIntroSeal',
    signals: '.js-heroIntroSignals',
    signal: '.js-heroIntroSignal',
    heading: '.js-heroIntroHeading',
    word: '.js-heroIntroWord',
    bandSection: '.js-heroIntroBandSection',
    band: '.js-heroIntroBand',
    bandText: '.js-heroIntroBandText',
    row: '.js-heroIntroRow',
    carousel: '[data-focal-carousel]',
    /* Outside the root: the sections after the hero, hidden until the band has risen. */
    below: '.js-heroIntroBelow',
});

/** The prototype's three curves: glide for the moves, rise for the arrivals, pop for the small overshoot. */
const EASE = Object.freeze({
    glide: EASING_CURVES['ease-in-out-cubic'],
    rise: EASING_CURVES['ease-out-cubic'],
    pop: EASING_CURVES['ease-out-back'],
});

/**
 * One knob for the pace of the whole intro: every delay and duration below is multiplied by it, so the
 * choreography keeps its rhythm. 1 is the designers' prototype; 0.85 plays it about a sixth faster.
 */
const TEMPO = 0.85;

/**
 * Milliseconds at tempo 1, from the prototype. Every cue of the features scene is an offset from
 * featuresScene, the moment the hero starts moving into its resting layout.
 */
const TIMING = Object.freeze({
    /* The seal's own clock. Its parts finish drawing at about 55% of it, which is the prototype's shape. */
    logoScene: 3000,
    /* A phone reads the same moments on a shorter clock: a seal alone on a white screen tires fast. */
    logoScenePhone: 2300,
    /* When the hero starts moving. Not the end of the seal's clock: the drawing is done at about 55% of it, and the wait after the last arrival read as a stall. These leave a single beat (70 ms) and no more. */
    featuresScene: 2150,
    featuresScenePhone: 1420,
    /* A short viewport has no room for a big seal: the band follows the title at once while the seal keeps drawing on its own clock. */
    logoSceneCompact: 700,
    sealDrawSmall: 1800,
    wordCues: [200, 360, 520, 950, 1110, 1270],
    /* The title words on a phone, under the big seal: the same rhythm as the desktop cues, inside the shorter scene. */
    wordCuesPhone: [250, 350, 450, 600, 700, 800],
    wordDuration: 550,
    wordFadeDuration: 400,
    sealFadeDelay: 150,
    sealFadeDuration: 1200,
    signalsStart: 1000,
    /* A phone keeps its first screen to the seal and the title: the trust lines arrive as the two land, so this cue is an offset from the features scene. */
    signalsStartPhone: 500,
    signalsStep: 160,
    signalFadeDuration: 450,
    signalPopDuration: 600,
    shrinkDuration: 850,
    cardsOffset: 380,
    /* On a phone the seal and the trust lines glide up through the row area: the band waits until they are nearly home. */
    bandLagPhone: 300,
    cardDuration: 700,
    cardStagger: 55,
    bandDuration: 650,
    bandTextOffsets: [820, 1020],
    bandTextDuration: 750,
    bandTextFadeDuration: 600,
    /* Below md the marquee rows drift in over the rising band, one after the other, where a wider screen has its cards. */
    rowStagger: 90,
    rowDuration: 500,
    rowFadeDuration: 400,
});

/** Pixels. The big seal of the logo scene, the air under it, and how far each part travels. */
const LAYOUT = Object.freeze({
    /* The seal of the logo scene: two and a half times its resting size at XL, the same ratio as on a phone. */
    bigSeal: 220,
    /* A phone grows the seal alone: the title has no room beside a bigger one, so it waits for the landing. */
    bigSealPhone: 200,
    bigGap: 40,
    signalsMargin: 8,
    wordRise: 14,
    signalRise: 10,
    bandTextRise: 26,
    rowRise: 12,
    /* The first arrangement at XL: four cards in focus and the slivers beside them. The rest stay lazy. */
    decodedCards: 9,
    skipScrollY: 24,
    /* Under this viewport height (a phone on its side, a small window) the compact choreography plays. */
    compactHeight: 600,
});

/**
 * Hold the page still while the logo scene plays, so the reader cannot scroll into the band before
 * the cards exist. Set to false to let the page scroll from the first frame. The hold ends when the
 * features scene begins, and it intercepts input instead of hiding the scrollbar, so nothing reflows.
 */
const HOLD_SCROLL_DURING_LOGO_SCENE = true;

/** The keys that scroll a page, held with the wheel and the finger while the hold is on. */
const SCROLL_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Spacebar']);

/**
 * Everything the running intro owns, so one call can end it early and leave the resting hero.
 * @typedef {object} Intro
 * @property {import('../../modules/motion/run.js').Run} run Every animation of the timeline, the clocks included, and the signal of the scroll hold and the early-finish listeners.
 * @property {HTMLElement} below The content after the hero, hidden until the band has risen.
 * @property {HTMLElement|null} carousel The focal carousel root, rendered or not.
 * @property {boolean} finished Whether the resting hero is in place.
 */

/** @type {Intro|null} */
let intro = null;

/**
 * @param {number} milliseconds A TIMING value at tempo 1.
 * @returns {number} The same cue at the current tempo.
 */
const tempo = (milliseconds) => Math.round(milliseconds * TEMPO);

/** Hold the first keyframe through the delay; the natural styles take over at the end. */
const FILL = 'backwards';

/**
 * @typedef {object} Parts The rendered elements the intro moves, at the current breakpoint.
 * @property {HTMLElement} seal The seal link, which grows in the logo scene and holds the drawn seal.
 * @property {HTMLElement[]} signalBlocks The trust-signal blocks: the left and right columns from md up, one centered block below.
 * @property {HTMLElement[]} signals The trust-signal lines that pop in.
 * @property {HTMLElement} heading The page title.
 * @property {HTMLElement[]} words The title words, one span each.
 * @property {HTMLElement} bandSection The industry-leaders section.
 * @property {HTMLElement} band The section's dark background, which rises.
 * @property {HTMLElement[]} bandTexts The heading and paragraphs on the band.
 * @property {HTMLElement[]} rows The marquee rows (below md).
 * @property {HTMLElement|null} carousel The focal carousel root (md up).
 * @property {HTMLElement} below The content after the hero, hidden until the band has risen.
 */

/**
 * @param {HTMLElement} root
 * @returns {Parts|null} Null when a required part is missing.
 */
function collectParts(root) {
    /**
     * @param {string} selector
     * @returns {HTMLElement[]} The rendered matches; a part hidden at this breakpoint has no offsetParent.
     */
    const all = (selector) => [...root.querySelectorAll(selector)].filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
    const seal = root.querySelector(SELECTOR.seal);
    const heading = root.querySelector(SELECTOR.heading);
    const bandSection = root.querySelector(SELECTOR.bandSection);
    const band = root.querySelector(SELECTOR.band);
    const below = document.querySelector(SELECTOR.below);
    if (!(seal instanceof HTMLElement) || !(heading instanceof HTMLElement) || !(bandSection instanceof HTMLElement) || !(band instanceof HTMLElement) || !(below instanceof HTMLElement)) {
        return null;
    }
    const carousel = root.querySelector(SELECTOR.carousel);

    return {
        seal,
        signalBlocks: all(SELECTOR.signals),
        signals: all(SELECTOR.signal),
        heading,
        words: all(SELECTOR.word),
        bandSection,
        band,
        bandTexts: all(SELECTOR.bandText),
        rows: all(SELECTOR.row),
        carousel: carousel instanceof HTMLElement && carousel.offsetParent !== null ? carousel : null,
        below,
    };
}

/**
 * Hand the card entrance back to the carousel's own on-screen trigger: for a carousel that was
 * hidden while the intro played (a phone turned wide later), and for a page that skipped the intro.
 * A carousel that already played its entrance ignores the call.
 * @param {HTMLElement|null} carousel
 */
function releaseCarousel(carousel) {
    if (!carousel) {
        return;
    }
    carousel.dataset.revealTrigger = 'visible';
    if (carousel.focalCarouselInstance) {
        carousel.focalCarouselInstance.armEntrance();
    }
}

/**
 * The resting hero at once: for a reader who arrives scrolled down, and for a page without the parts.
 * @param {HTMLElement} root
 */
function showAtRest(root) {
    root.removeAttribute('data-hero-intro-pending');
    releaseCarousel(root.querySelector(SELECTOR.carousel));
}

/**
 * End the intro where it stands: every animation jumps to its last keyframe, which is the resting
 * layout, the hold and the listeners go, the content below shows, and the seal shows its full drawing.
 * Runs once. Cut short (a focus move into the hero, a breakpoint change), the card entrance ends
 * with it; at the natural end a carousel that is still emerging plays on.
 * @param {boolean} cut Whether the intro ends before its time.
 */
function finishIntro(cut) {
    if (intro === null || intro.finished) {
        return;
    }
    intro.finished = true;
    intro.run.finish();
    intro.below.classList.remove('invisible');
    if (cut && intro.carousel?.offsetParent) {
        focalCarousel(intro.carousel).then((instance) => instance?.skipEntrance());
    } else {
        releaseCarousel(intro.carousel);
    }
}

const cutShort = () => finishIntro(true);

/**
 * Widest child of a block, so the trust signals can move outward without leaving the container.
 * @param {HTMLElement} block
 * @returns {number}
 */
function widestChild(block) {
    return [...block.children].reduce((widest, child) => Math.max(widest, child.getBoundingClientRect().width), 0);
}

/**
 * Trust signals in the prototype's order: the top row left then right, then the bottom row.
 * @param {HTMLElement[]} blocks
 * @param {HTMLElement[]} signals
 * @returns {HTMLElement[]}
 */
function signalOrder(blocks, signals) {
    if (blocks.length !== 2) {
        return signals;
    }
    const [left, right] = blocks.map((block) => signals.filter((signal) => block.contains(signal)));
    const rows = Math.max(left.length, right.length);
    const ordered = [];
    for (let row = 0; row < rows; row++) {
        for (const column of [left, right]) {
            if (column[row]) {
                ordered.push(column[row]);
            }
        }
    }

    return ordered;
}

/**
 * Fade and rise in: opacity finishes early, the rise runs the whole duration.
 * @param {HTMLElement} element
 * @param {number} delay
 * @param {number} duration
 * @param {number} fadeDuration
 * @param {number} rise
 * @returns {Animation}
 */
function arrive(element, delay, duration, fadeDuration, rise) {
    return element.animate(
        [
            {opacity: 0, transform: `translateY(${rise}px)`},
            {opacity: 1, offset: fadeDuration / duration},
            {opacity: 1, transform: 'none'},
        ],
        {delay, duration, easing: EASE.rise, fill: FILL}
    );
}

/**
 * Grow the seal for the logo scene and glide it back into its slot. The artwork is laid out at the big
 * size and scaled down to rest on a layer of its own while the intro plays: WebKit rasters a composited
 * layer at its layout size and lets the compositor scale it, so a seal laid out at its resting size was a
 * stretched bitmap through the whole logo scene in Safari. Laid out big, its raster is sharp at the big
 * size and only ever scaled down. The resting seal gets its plain markup back when the intro ends.
 * @param {HTMLElement} seal
 * @param {string} transform The big state of the seal's box.
 * @param {number} bigSize Pixels, the seal on screen during the logo scene.
 * @param {number} restSize Pixels, the seal's box.
 * @param {{delay: number, duration: number, easing: string, fill: string}} shrink The glide's timing.
 * @param {import('../../modules/motion/run.js').Run} run
 */
function growSeal(seal, transform, bigSize, restSize, shrink, run) {
    run.add(seal.animate([{transform}, {transform: 'none'}], shrink));
    const art = seal.querySelector('svg');
    if (!(art instanceof SVGElement)) {
        return;
    }
    const layout = {width: `${bigSize}px`, height: `${bigSize}px`, 'transform-origin': 'top left', transform: `scale(${restSize / bigSize})`, 'will-change': 'transform'};
    for (const [property, value] of Object.entries(layout)) {
        art.style.setProperty(property, value);
    }
    run.signal.addEventListener(
        'abort',
        () => {
            for (const property of Object.keys(layout)) {
                art.style.removeProperty(property);
            }
        },
        {once: true}
    );
}

/**
 * Swallow every scroll input until the release timer runs out. Scrollbar drags cannot be
 * intercepted; a drag ends the hold early instead of fighting the reader. A timer, not an
 * animation: WebKit never finishes an animation without keyframe properties, and the hold stuck.
 * @param {number} duration Milliseconds.
 * @param {AbortSignal} finish Ends the hold early, with the intro.
 */
function holdScroll(duration, finish) {
    const release = new AbortController();
    const {signal} = release;
    const options = {passive: false, signal};
    /**
     * @param {Event} event
     * @returns {void}
     */
    const swallow = (event) => event.preventDefault();
    window.addEventListener('wheel', swallow, options);
    window.addEventListener('touchmove', swallow, options);
    window.addEventListener(
        'keydown',
        (event) => {
            if (SCROLL_KEYS.has(event.key) && !(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
                event.preventDefault();
            }
        },
        options
    );
    window.addEventListener('scroll', () => release.abort(), {once: true, signal});

    const timer = setTimeout(() => release.abort(), duration);
    signal.addEventListener('abort', () => clearTimeout(timer), {once: true});
    forwardAbort(finish, release);
}

/**
 * @param {HTMLElement} root
 * @param {Parts} parts
 */
function play(root, parts) {
    const phone = isMobile();
    // A short viewport has no room for the big seal: the band follows the title at once.
    const compact = window.innerHeight < LAYOUT.compactHeight;
    const splash = !compact;
    // A phone has no room to grow the seal beside the title: its logo scene is the seal alone, and the title arrives as the seal lands.
    const phoneSplash = splash && phone;
    const featuresAt = tempo(compact ? TIMING.logoSceneCompact : phone ? TIMING.featuresScenePhone : TIMING.featuresScene);
    const sealDraw = splash ? tempo(phone ? TIMING.logoScenePhone : TIMING.logoScene) : tempo(TIMING.sealDrawSmall);
    const run = createRun();
    intro = {run, below: parts.below, carousel: root.querySelector(SELECTOR.carousel), finished: false};

    // Resting geometry, all of it read here, before any animation dirties style again.
    const sealRect = parts.seal.getBoundingClientRect();
    const headingRect = parts.heading.getBoundingClientRect();
    const rootRect = phoneSplash ? root.getBoundingClientRect() : null;
    // The band waits below its section, where the clip box hides even its transparent top: it rises the whole way while the cards come in.
    const bandOffset = parts.bandSection.getBoundingClientRect().height;
    // How far the trust signals can move outward before a line leaves the container.
    const signalsSlack = splash && !phone
        ? parts.signalBlocks.reduce((least, block) => Math.min(least, block.getBoundingClientRect().width - widestChild(block)), Infinity)
        : 0;

    const wordCues = phoneSplash ? TIMING.wordCuesPhone : TIMING.wordCues;
    parts.words.forEach((word, index) => {
        const cue = wordCues[index] ?? wordCues.at(-1);
        run.add(arrive(word, tempo(cue), tempo(TIMING.wordDuration), tempo(TIMING.wordFadeDuration), LAYOUT.wordRise));
    });
    run.add(parts.seal.animate([{opacity: 0}, {opacity: 1}], {delay: tempo(TIMING.sealFadeDelay), duration: tempo(TIMING.sealFadeDuration), easing: EASE.rise, fill: FILL}));
    run.add(...drawSeal(parts.seal, sealDraw));
    const signalsAt = phoneSplash ? featuresAt + tempo(TIMING.signalsStartPhone) : 0;
    const signalsStart = phoneSplash ? 0 : TIMING.signalsStart;
    signalOrder(parts.signalBlocks, parts.signals).forEach((signal, index) => {
        const delay = signalsAt + tempo(signalsStart + index * TIMING.signalsStep);
        run.add(signal.animate([{opacity: 0}, {opacity: 1}], {delay, duration: tempo(TIMING.signalFadeDuration), easing: EASE.rise, fill: FILL}));
        run.add(signal.animate([{transform: `translateY(${LAYOUT.signalRise}px)`}, {transform: 'none'}], {delay, duration: tempo(TIMING.signalPopDuration), easing: EASE.pop, fill: FILL}));
    });

    const shrink = {delay: featuresAt, duration: tempo(TIMING.shrinkDuration), easing: EASE.glide, fill: FILL};

    if (phoneSplash) {
        // The big seal in the middle of the screen with the title under it, both centered, then one glide into the resting row.
        const bigSeal = LAYOUT.bigSealPhone;
        const groupHeight = bigSeal + LAYOUT.bigGap + headingRect.height;
        const groupTop = Math.max(sealRect.top, (window.innerHeight - groupHeight) / 2);
        const center = rootRect.left + rootRect.width / 2;
        const sealShiftX = center - (sealRect.left + sealRect.width / 2);
        const sealShiftY = groupTop + bigSeal / 2 - (sealRect.top + sealRect.height / 2);
        const headingShift = `translate(${center - (headingRect.left + headingRect.width / 2)}px, ${groupTop + bigSeal + LAYOUT.bigGap - headingRect.top}px)`;

        growSeal(parts.seal, `translate(${sealShiftX}px, ${sealShiftY}px) scale(${bigSeal / sealRect.height})`, bigSeal, sealRect.height, shrink, run);
        run.add(parts.heading.animate([{transform: headingShift}, {transform: 'none'}], shrink));
    } else if (splash) {
        // The big seal, centered in the viewport with the headline under it, then one shrink into the resting layout.
        const growth = Number.isFinite(signalsSlack) ? Math.max(0, 2 * (signalsSlack - LAYOUT.signalsMargin)) : LAYOUT.bigSeal - sealRect.height;
        const bigSeal = Math.min(LAYOUT.bigSeal, sealRect.height + growth);
        const scale = bigSeal / sealRect.height;
        const groupHeight = bigSeal + LAYOUT.bigGap + headingRect.height;
        const groupTop = Math.max(sealRect.top, (window.innerHeight - groupHeight) / 2);
        const sealShift = groupTop + bigSeal / 2 - (sealRect.top + sealRect.height / 2);
        const headingShift = groupTop + bigSeal + LAYOUT.bigGap - headingRect.top;
        const signalsShift = (bigSeal - sealRect.height) / 2;

        growSeal(parts.seal, `translateY(${sealShift}px) scale(${scale})`, bigSeal, sealRect.height, shrink, run);
        run.add(parts.heading.animate([{transform: `translateY(${headingShift}px)`}, {transform: 'none'}], shrink));
        parts.signalBlocks.forEach((block, index) => {
            const direction = index === 0 ? -1 : 1;
            run.add(block.animate([{transform: `translate(${direction * signalsShift}px, ${sealShift}px)`}, {transform: 'none'}], shrink));
        });
    }

    // The photos of the first arrangement decode off the main thread now, so those cards emerge with their faces on.
    const firstCards = parts.carousel ? [...parts.carousel.querySelectorAll('[data-focal-carousel-item]')].slice(0, LAYOUT.decodedCards) : [];
    for (const image of firstCards.flatMap((card) => [...card.querySelectorAll('img')])) {
        image.decode().catch(() => {});
    }

    const bandAt = featuresAt + (phoneSplash ? tempo(TIMING.bandLagPhone) : 0);
    const cardsAt = bandAt + tempo(TIMING.cardsOffset);
    const bandRise = parts.band.animate([{transform: `translateY(${bandOffset}px)`}, {transform: 'none'}], {delay: cardsAt, duration: tempo(TIMING.bandDuration), easing: EASE.rise, fill: FILL});
    run.add(bandRise);
    // The content after the hero shows once the band covers its section, whatever the viewport height; an intro cut short shows it at once.
    settle(bandRise).then(() => parts.below.classList.remove('invisible'));
    parts.rows.forEach((row, index) => {
        run.add(arrive(row, cardsAt + tempo(index * TIMING.rowStagger), tempo(TIMING.rowDuration), tempo(TIMING.rowFadeDuration), LAYOUT.rowRise));
    });
    parts.bandTexts.forEach((text, index) => {
        const offset = TIMING.bandTextOffsets[index] ?? TIMING.bandTextOffsets.at(-1);
        run.add(arrive(text, bandAt + tempo(offset), tempo(TIMING.bandTextDuration), tempo(TIMING.bandTextFadeDuration), LAYOUT.bandTextRise));
    });

    if (phone) {
        // No hold on a phone: a reader who scrolls away gets the resting page at once. A load can fire scroll at 0, so read the position.
        window.addEventListener(
            'scroll',
            () => {
                if (window.scrollY > LAYOUT.skipScrollY) {
                    cutShort();
                }
            },
            {signal: run.signal}
        );
    } else if (HOLD_SCROLL_DURING_LOGO_SCENE) {
        holdScroll(featuresAt, run.signal);
    }

    if (parts.carousel) {
        const carousel = parts.carousel;
        // The cards' cue is a timer: an intro cut short settles the carousel itself (see finishIntro).
        const cue = new Promise((resolve) => {
            const timer = setTimeout(resolve, cardsAt);
            run.signal.addEventListener('abort', () => clearTimeout(timer), {once: true});
        });
        Promise.all([cue, focalCarousel(carousel)]).then(([, instance]) => {
            if (!intro?.finished) {
                instance?.runReveal({duration: tempo(TIMING.cardDuration), staggerDelay: tempo(TIMING.cardStagger)});
            }
        });
    }

    // Keyboard focus into a hero that is still moving, or a breakpoint change under its measured geometry, ends the intro at once.
    root.addEventListener('focusin', cutShort, {signal: run.signal});
    for (const width of Object.values(BREAKPOINTS)) {
        window.matchMedia(`(min-width: ${width}px)`).addEventListener('change', cutShort, {signal: run.signal});
    }

    // Same task as the animation start: the attribute removal and the first keyframes land in one style recalc. The content below keeps its hidden state by class from here.
    parts.below.classList.add('invisible');
    root.removeAttribute('data-hero-intro-pending');

    run.settled().then(() => finishIntro(false));
}

/**
 * Play the intro on a fresh arrival at the top of the page; otherwise show the resting hero.
 * The scroll position is read after the first frame: a restored position or a fragment jump
 * lands after the script runs, and an intro under a reader who is already further down plays to nobody.
 * A tab opened in the background waits for the first look, and the parts are collected only then,
 * at the breakpoint the intro will play at.
 */
function init() {
    const root = document.querySelector(SELECTOR.root);
    if (!(root instanceof HTMLElement)) {
        return;
    }
    if (window.location.hash !== '') {
        showAtRest(root);
        return;
    }
    requestAnimationFrame(() => {
        if (window.scrollY > LAYOUT.skipScrollY) {
            showAtRest(root);
            return;
        }
        const start = () => {
            const parts = collectParts(root);
            if (parts === null) {
                showAtRest(root);
                return;
            }
            play(root, parts);
        };
        if (document.visibilityState === 'hidden') {
            document.addEventListener('visibilitychange', start, {once: true});
            return;
        }
        start();
    });
}

document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
